import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Security, QrCode2, CheckCircle } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const steps = ['Scansiona QR Code', 'Verifica codice'];

export default function TwoFactorSetup() {
  const { complete2faSetup, logout } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);

  useEffect(() => {
    api.post('/auth/2fa/setup/')
      .then((res) => {
        setQrCode(res.data.qr_code);
        setSecret(res.data.secret);
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Errore nel setup 2FA');
      })
      .finally(() => setSetupLoading(false));
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/2fa/confirm/', { otp_code: otpCode });
      setActiveStep(2);
      setTimeout(() => {
        complete2faSetup();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 30%, #1E88E5 60%, #42A5F5 100%)',
        p: 2,
      }}
    >
      <Fade in timeout={600}>
        <Paper
          elevation={24}
          sx={{
            p: 5,
            width: '100%',
            maxWidth: 500,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.97)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Security sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={700}>
              Configurazione 2FA obbligatoria
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Per la sicurezza del tuo account, configura l'autenticazione a due fattori con Google Authenticator o un'app compatibile.
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {setupLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : activeStep === 2 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" fontWeight={600}>
                2FA attivata con successo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Accesso in corso...
              </Typography>
            </Box>
          ) : (
            <>
              {activeStep === 0 && (
                <Box sx={{ textAlign: 'center' }}>
                  {qrCode && (
                    <Box
                      component="img"
                      src={qrCode}
                      alt="QR Code 2FA"
                      sx={{ width: 200, height: 200, mx: 'auto', mb: 2 }}
                    />
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Scansiona il QR code con Google Authenticator
                  </Typography>
                  {secret && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontFamily: 'monospace',
                        bgcolor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        wordBreak: 'break-all',
                        mb: 3,
                      }}
                    >
                      Chiave manuale: {secret}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setActiveStep(1)}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
                    }}
                  >
                    Ho scansionato il QR code
                  </Button>
                </Box>
              )}

              {activeStep === 1 && (
                <form onSubmit={handleConfirm}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <QrCode2 sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body1" fontWeight={500}>
                      Inserisci il codice a 6 cifre dall'app
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Codice OTP"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    required
                    disabled={loading}
                    sx={{ mb: 2 }}
                    inputProps={{
                      maxLength: 6,
                      style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' },
                    }}
                  />

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || otpCode.length < 6}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Attiva 2FA'}
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setActiveStep(0)}
                    sx={{ mt: 1 }}
                    disabled={loading}
                  >
                    Torna indietro
                  </Button>
                </form>
              )}
            </>
          )}

          <Button
            fullWidth
            variant="text"
            color="inherit"
            onClick={logout}
            sx={{ mt: 2, color: 'text.secondary' }}
          >
            Logout
          </Button>
        </Paper>
      </Fade>
    </Box>
  );
}
